import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { StoreService } from '../../../services/store.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import { finalize, of, switchMap } from 'rxjs';
import PaymentMethodTypes = Boo.ApisExternal.Store.Enums.PaymentMethodTypes;
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import PaymentMethod = Boo.ApisExternal.Store.Objects.AllowedPaymentMethod;
import { environment } from '../../../../environments/environment';
import { AuthNetSettings, AuthNetResult } from '../authnet-hosted-form/authnet-hosted-form.component';

@Component({
  selector: 'app-components-partner-payment-method',
  templateUrl: './payment-method.component.html'
})
export class PaymentMethodComponent implements OnInit {
  @ViewChild('authNetModal') authNetModal: TemplateRef<any>;
  partner: Boo.Objects.Partner;
  paymentMethods: PaymentMethod[] = [];
  authNetSettings: AuthNetSettings;
  isLoading = true;

  paymentMethodTypes_AuthorizeNetPartnerCreditCard = PaymentMethodTypes.AuthorizeNetPartnerCreditCard;

  constructor (
    private modalService: NgbModal,
    private sessionStorageService: SessionStorageService,
    private storeService: StoreService) {
  }

  ngOnInit(): void {
    this.sessionStorageService.getPartner()
      .pipe(
        finalize(() => this.isLoading = false),
        switchMap((partner: Boo.Objects.Partner) => {
          this.partner = partner;
          return this.partner?.StorefrontId
            ? this.storeService.getPaymentMethods(this.partner.StorefrontId)
            : of([]);
        })
      )
      .subscribe({
        next: (paymentMethods: PaymentMethod[]) => {
          this.paymentMethods = [...paymentMethods]
            .sort((a, b) => (b.IsCustomerDefault === a.IsCustomerDefault ? 0 : b.IsCustomerDefault ? 1 : -1))
            .map((paymentMethod) => {
              this.setDisplayProperties(paymentMethod);
              return paymentMethod;
            });
        },
        error: (err) => toastr.error(err)
      });
  }

  editPartnerCard() {
    const modalRef = this.modalService.open(this.authNetModal);
    modalRef.result.finally(() => this.authNetSettings = null);

    this.storeService.getAuthorizeNetHostedFormInfo(this.partner.StorefrontId, environment.authNetIframeCommunicator)
      .subscribe({
        next: (settings) => {
          this.authNetSettings = {
            actionUrl: settings.PaymentProfileId ? environment.authNetEditPaymentUrl : environment.authNetAddPaymentUrl,
            token: settings.Token,
            paymentProfileId: settings.PaymentProfileId
          };
        },
        error: (err) => {
          toastr.error('Unable to load payment information');
          modalRef.dismiss();
        }
      });
  }

  clearSettings() {
    this.authNetSettings = null;
    this.modalService.dismissAll();
  }

  reloadSettings(result: AuthNetResult) {
    this.authNetSettings = null;
    this.modalService.dismissAll();
    
    if (result === 'success') {
      this.storeService.updateAuthorizeNetProfile(this.partner.StorefrontId)
      .subscribe({
        next: () => {
          toastr.success('Payment information updated');
        },
        error: () => {
          toastr.error('Unable to save payment information');
        }
      });
    }
  }

  private setDisplayProperties(paymentMethod: PaymentMethod): void {
    switch (paymentMethod.PaymentMethodType) {
      case PaymentMethodTypes.Imported:
        paymentMethod.DisplayName = 'Imported';
        paymentMethod.IconClass = 'fa fa-file-import';
        break;
      case PaymentMethodTypes.Free:
        paymentMethod.DisplayName = 'Free';
        paymentMethod.IconClass = 'fa fa-gift';
        break;
      case PaymentMethodTypes.Invoice:
        paymentMethod.DisplayName = 'Invoice';
        paymentMethod.IconClass = 'fa fa-file-invoice-dollar';
        break;
      case PaymentMethodTypes.AuthorizeNetCreditCard:
        paymentMethod.DisplayName = 'Credit Card';
        paymentMethod.IconClass = 'far fa-credit-card';
        break;
      case PaymentMethodTypes.AuthorizeNetPartnerCreditCard:
        paymentMethod.DisplayName = 'Partner Credit Card';
        paymentMethod.IconClass = 'far fa-credit-card';
        break;
      default:
        paymentMethod.DisplayName = 'Unknown';
        paymentMethod.IconClass = 'fa fa-question';
        break;
    }
  }
}